import { motion } from "framer-motion";

/**
 * Lymphie — the soft purple robot companion.
 * Pure SVG so it scales crisply at any size, no image dependency.
 *
 * Props:
 *   size       — pixel size (default 64)
 *   animated   — enable floating + blinking + antenna pulse (default true)
 *   waving     — hand waves gently when true (default false)
 */
export default function Lymphie({ size = 64, animated = true, waving = false, className = "", ...rest }) {
  const wrapAnim = animated ? { y: [0, -4, 0] } : undefined;
  const wrapTrans = animated ? { duration: 3.4, repeat: Infinity, ease: "easeInOut" } : undefined;

  return (
    <motion.div
      className={`inline-block ${className}`}
      style={{ width: size, height: size }}
      animate={wrapAnim}
      transition={wrapTrans}
      data-testid="lymphie-avatar"
      {...rest}
    >
      <svg viewBox="0 0 120 130" width={size} height={size} xmlns="http://www.w3.org/2000/svg" aria-label="Lymphie">
        <defs>
          <linearGradient id="lymBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="#CDB6EA" />
            <stop offset="55%" stopColor="#B39BD8" />
            <stop offset="100%" stopColor="#9E82C9" />
          </linearGradient>
          <linearGradient id="lymFace" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FDFBFF" />
            <stop offset="100%" stopColor="#F3ECFA" />
          </linearGradient>
          <radialGradient id="lymShadow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="rgba(120,90,175,0.28)" />
            <stop offset="100%" stopColor="rgba(120,90,175,0)" />
          </radialGradient>
          <linearGradient id="lymHeart" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F0D2E4" />
            <stop offset="100%" stopColor="#D4A2C8" />
          </linearGradient>
        </defs>

        {/* Soft glow halo */}
        <ellipse cx="60" cy="118" rx="34" ry="6" fill="url(#lymShadow)" />

        {/* Antenna */}
        <line x1="60" y1="24" x2="60" y2="8" stroke="#9E82C9" strokeWidth="3" strokeLinecap="round" />
        <motion.g
          animate={animated ? { scale: [1, 1.14, 1] } : undefined}
          transition={animated ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" } : undefined}
          style={{ transformOrigin: "60px 8px" }}
        >
          <path
            d="M60 3.5 c 2.4 -3 7.4 -3 7.4 1.6 c 0 3.4 -4.6 6.4 -7.4 8.4 c -2.8 -2 -7.4 -5 -7.4 -8.4 c 0 -4.6 5 -4.6 7.4 -1.6 z"
            fill="url(#lymHeart)"
            stroke="#B78CB0"
            strokeWidth="1"
          />
        </motion.g>

        {/* Head */}
        <rect x="22" y="24" width="76" height="62" rx="30" fill="url(#lymBody)" />
        {/* Head highlight */}
        <ellipse cx="42" cy="38" rx="16" ry="7" fill="#FFFFFF" opacity="0.18" />

        {/* Face window */}
        <rect x="30" y="34" width="60" height="42" rx="20" fill="url(#lymFace)" />

        {/* Cheeks */}
        <circle cx="41" cy="60" r="4.2" fill="#F5BFD0" opacity="0.85" />
        <circle cx="79" cy="60" r="4.2" fill="#F5BFD0" opacity="0.85" />

        {/* Eyes — happy closed arcs, blink on animated */}
        <motion.g
          animate={animated ? { scaleY: [1, 1, 0.15, 1, 1, 1, 0.15, 1] } : undefined}
          transition={animated ? { duration: 5.2, repeat: Infinity, ease: "easeInOut", times: [0, 0.42, 0.44, 0.46, 0.7, 0.82, 0.84, 1] } : undefined}
          style={{ transformOrigin: "60px 54px" }}
        >
          <path d="M43 54 q 4 -5 8 0" stroke="#5B4275" strokeWidth="2.8" strokeLinecap="round" fill="none" />
          <path d="M69 54 q 4 -5 8 0" stroke="#5B4275" strokeWidth="2.8" strokeLinecap="round" fill="none" />
        </motion.g>

        {/* Smile */}
        <path d="M53 64 q 7 6 14 0" stroke="#5B4275" strokeWidth="2.6" strokeLinecap="round" fill="none" />

        {/* Body */}
        <rect x="30" y="80" width="60" height="34" rx="17" fill="url(#lymBody)" />
        {/* Body highlight */}
        <ellipse cx="45" cy="88" rx="12" ry="4" fill="#FFFFFF" opacity="0.15" />
        {/* Chest heart */}
        <motion.path
          d="M60 92 c 2.4 -3 7.4 -3 7.4 1.6 c 0 3.4 -4.6 6.4 -7.4 8.4 c -2.8 -2 -7.4 -5 -7.4 -8.4 c 0 -4.6 5 -4.6 7.4 -1.6 z"
          fill="url(#lymHeart)"
          stroke="#B78CB0"
          strokeWidth="0.9"
          animate={animated ? { scale: [1, 1.08, 1] } : undefined}
          transition={animated ? { duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.4 } : undefined}
          style={{ transformOrigin: "60px 97px" }}
        />

        {/* Little arms — right hand waves when `waving` */}
        <motion.g
          animate={waving ? { rotate: [0, -22, 0, -22, 0] } : undefined}
          transition={waving ? { duration: 1.2, repeat: Infinity, ease: "easeInOut" } : undefined}
          style={{ transformOrigin: "94px 88px" }}
        >
          <circle cx="96" cy="90" r="6.5" fill="url(#lymBody)" />
        </motion.g>
        <circle cx="24" cy="90" r="6.5" fill="url(#lymBody)" />
      </svg>
    </motion.div>
  );
}
